import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { horodatyApi } from '@/lib/utils/horodaty-api';
import { setAuthToken } from '@/lib/utils/api';
import { forgetSessionLocale } from '@/lib/i18n';
import { reconcileLocaleWithUser } from './locale-context';
import { useApiCache } from './api-cache-context';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const { getCache, setCache, clearAllCache } = useApiCache();

    // État initial depuis l'injection PHP (pas de requête !)
    const [isAuthenticated, setIsAuthenticated] = useState(() => window.__AUTH_STATE__?.isAuthenticated ?? false);
    const [user, setUser] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Charger les données utilisateur si authentifié (en background)
    useEffect(() => {
        if (!isAuthenticated) return;

        setIsLoading(true);
        horodatyApi
            .getUser()
            .then((res) => {
                const userData = res.data?.data ?? null;
                setUser(userData);
                // Cache with userId namespace for future requests
                const userId = userData?.uniq_id ?? userData?.id;
                if (userId) {
                    setCache(`user:${userId}`, userData, 'user');
                }
                // Bascule la langue à chaud si le compte en porte une autre que
                // celle affichée (typiquement un appareil sur lequel l'utilisateur
                // n'a jamais choisi).
                reconcileLocaleWithUser(userData);
            })
            .catch(() => {
                // Token invalide/expiré → déconnecter. Le serveur croyait la
                // session valide (window.__AUTH_STATE__), la langue du compte a
                // donc été chargée : forgetSessionLocale() la retire aussi de
                // l'affichage, sinon la page de connexion resterait traduite.
                forgetSessionLocale();
                setIsAuthenticated(false);
                setUser(null);
            })
            .finally(() => setIsLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuthenticated]);

    // Écouter l'événement de session expirée depuis l'intercepteur Axios
    useEffect(() => {
        const handleSessionExpired = () => {
            // Même nettoyage qu'à la déconnexion. La langue du compte part avec
            // la session, affichage compris : RequireAuth redirige aussitôt vers
            // la page de connexion, qui doit être dans la langue du poste.
            forgetSessionLocale();
            setIsAuthenticated(false);
            setUser(null);
        };

        window.addEventListener('auth:session-expired', handleSessionExpired);
        return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
    }, []);

    const login = useCallback(async (credentials) => {
        const res = await horodatyApi.login(credentials);
        setIsAuthenticated(true);
        // Charger l'utilisateur après connexion
        const userRes = await horodatyApi.getUser();
        const userData = userRes.data?.data ?? null;

        // Vérifier si l'utilisateur est rattaché à une entreprise (sauf admin qui peut accéder sans)
        const hasCompany = Boolean(userData?.company_uniq_id ?? userData?.company?.uniq_id);
        const isAdmin = userData?.role === 'admin';

        if (!hasCompany && !isAdmin) {
            // Déconnecter l'utilisateur et lever une erreur
            await horodatyApi.logout();
            forgetSessionLocale();
            setIsAuthenticated(false);
            setUser(null);
            throw new Error("Votre compte n'est pas rattaché à une entreprise. Contactez votre administrateur.");
        }

        setUser(userData);

        // La langue du compte s'applique dès cette session, à chaud : un
        // utilisateur qui se connecte depuis un nouvel appareil ne voit pas sa
        // session dans la langue du navigateur.
        reconcileLocaleWithUser(userData);

        return res;
    }, []);

    /**
     * Seul point de sortie de l'application : les boutons « Déconnexion »
     * passent tous ici, pas par la route GET /logout du serveur, pour que le
     * nettoyage côté navigateur ait lieu avant la navigation.
     */
    const logout = useCallback(async () => {
        // Best-effort : si la session serveur est déjà tombée (401), l'appel
        // échoue mais l'utilisateur veut quand même partir — on nettoie et on
        // sort, sans rester bloqué sur un écran connecté.
        try {
            await horodatyApi.logout();
        } catch (e) {}
        setAuthToken(null);
        clearAllCache(); // Clear all cached data on logout
        // La langue qui relevait de la session part avec elle : le poste revient
        // à la langue du navigateur, que la navigation qui suit ré-applique. Un
        // choix fait hors session, lui, est conservé.
        forgetSessionLocale();
        setIsAuthenticated(false);
        setUser(null);
        window.location.href = '/';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const refreshUser = useCallback(
        async (forceRefresh = false) => {
            if (!isAuthenticated) return null;

            try {
                const res = await horodatyApi.getUser();
                const userData = res.data?.data ?? null;
                setUser(userData);
                // Cache with userId namespace
                const userId = userData?.uniq_id ?? userData?.id;
                if (userId) {
                    setCache(`user:${userId}`, userData, 'user');
                }
                return userData;
            } catch {
                return null;
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isAuthenticated],
    );

    /**
     * @deprecated Utiliser `usePermissions().can(code)` à la place.
     * Conservée temporairement pour rétrocompatibilité.
     */
    const isOwnerOrAdmin = () => {
        if (!user) return false;
        return user.role === 'admin' || user.role === 'owner' || user.role === 'head_department';
    };

    return (
        <AuthContext.Provider
            value={{
                isAuthenticated,
                user,
                isLoading,
                isOwnerOrAdmin,
                login,
                logout,
                refreshUser,
                setIsAuthenticated,
                setUser,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
